// ============================================================
// AttackerPanel - Left panel: tabbed layout with Armory,
// Attack Controls, and character Overview.
// ============================================================
import { liveryByCombatant } from '../engine/roster/team';
import ModeStrip from './ModeStrip';
import { memo, useContext, useMemo, useState, useRef, useEffect, useSyncExternalStore } from 'react';
import clsx from '../utils/clsx';
import CombatantGallery from './CombatantGallery';
import ActorStatusCurtain from './ActorStatusCurtain';
import { RailPanelOpenContext } from './shell/ShellColumns';
import PanelIdentityHeader from './PanelIdentityHeader';
import CombatantSheetTabs from './CombatantSheetTabs';
import ActorStatusView, { actorEdits } from './ActorStatusView';
import { useRosterContext } from '../contexts/RosterContext';
import common from '../styles/common.module.css';
import styles from '../styles/AttackerPanel.module.css';
import AttackFooter from './AttackFooter';
import ActionDrum from './actions/ActionDrum';
import AttackerControlsTab from './AttackerControlsTab';
import ArmoryTab from './armoury/ArmoryTab';
import AttackerOverviewTab from './AttackerOverviewTab';
import { useAttacker } from '../contexts/AttackerContext';
import { useTargets } from '../contexts/TargetsContext';
import { useSession } from '../contexts/SessionContext';
import {
  resolveApplyConditionModifiers,
  resolveConditionModOptions,
  resolveConditionsBlockSheetTests,
  resolveRoundCountdown,
  resolveDetailedActorBodyMap,
  resolveStackPolicy,
} from '../engine/settings/resolve';
import { blockedReactionTests } from '../engine/reactionAvailability';
import useSheetEditor from '../hooks/useSheetEditor';
import { getViewerPrefs, setViewerPref, subscribeViewerPrefs } from '../utils/viewerPrefs';
import { peopleOf } from '../engine/roster/inTheFight';

// v7.1.0 - the acting character gets their WHOLE sheet, not just the
// half that shoots. Defender/Stats/Gear are the same components the
// target panel renders, pointed at whoever you are acting as.
const TABS = [
  { key: 'status', label: 'Status' },
  { key: 'armory', label: 'Armory' },
  { key: 'controls', label: 'Controls' },
  { key: 'overview', label: 'Overview' },
  { key: 'defender', label: 'Defender' },
  { key: 'stats', label: 'Stats' },
  { key: 'gear', label: 'Gear' },
];

/** The acting character's sheet tabs. */
const SHEET_TABS = new Set(['defender', 'stats', 'gear']);

function AttackerPanel() {
  const atk = useAttacker();
  const { turnTrackerState, rerollInitiative, impactsNow } = useTargets();
  const { roster, actorId, setActorId, actor, updateCombatantState } = useRosterContext();
  const actorLivery = useMemo(() => (actorId ? liveryByCombatant(roster)[actorId] : null), [roster, actorId]);
  const { isLocked, permissions } = useSession();
  const [panelTab, setPanelTab] = useState('status');
  const curtain = useHoverCurtain(!!(actor && atk.actorTarget) && panelTab !== 'status');
  const actorLocked = isLocked('editTarget');
  // Built ONCE, rendered wherever is live: the Status tab when it is
  // selected, the curtain's glass when the cursor is elsewhere.
  const actorStatusView = statusViewFor({
    actor,
    atk,
    roster,
    locked: actorLocked,
    permissions,
    updateCombatantState,
    turnTrackerState,
    rerollInitiative,
    impact: (actor && impactsNow?.[actor.id]) ?? null,
  });

  return (
    <div className={styles.panelContainer} onMouseEnter={curtain.onEnter} onMouseLeave={curtain.onLeave}>
      <ActorHeader
        actor={actor}
        actorTarget={atk.actorTarget}
        roster={roster}
        actorId={actorId}
        setActorId={setActorId}
        curtainAuto={curtain.auto}
      />
      {/* One strip (plan modes-rework): Solo | Squad for a brother, then the current mode's plates, then what is up. */}
      <ModeStrip
        actor={actor}
        livery={actorLivery}
        locked={isLocked('attack')}
        onToggleSolo={atk.onToggleStance}
        squad={atk.squadDrum}
        pressSquad={atk.pressSquad}
      />
      <TabBar panelTab={panelTab} onTab={setPanelTab} />
      {/* The viewport wrapper exists for the curtain (v8.247.2): the
          glass spans exactly the tab content region, so the footer and
          everything the ghost projects above it stay clear of each
          other - nothing gets half buried under the ATTACK bar. */}
      <div className={styles.tabViewport}>
        {/* The glass projection of the Status view (v8.247.0): present
            whenever the cursor is elsewhere, gone when it comes home. */}
        <ActorStatusCurtain visible={curtain.visible}>{actorStatusView}</ActorStatusCurtain>
        <div className={styles.tabContentArea}>
          <TabContent
            panelTab={panelTab}
            onOpenControls={() => setPanelTab('controls')}
            actor={actor}
            atk={atk}
            locked={isLocked('editProfiles')}
            permissions={permissions}
            statusView={actorStatusView}
          />
        </div>
      </div>
      <AttackBar atk={atk} isLocked={isLocked} roundCountdown={resolveRoundCountdown(permissions)} />
    </div>
  );
}

/** The actor's Status view, or null with nobody to show. */
function statusViewFor({ actor, atk, roster, locked, permissions, updateCombatantState, impact, ...tracker }) {
  if (!actor || !atk.actorTarget) return null;
  return (
    <ActorStatusView
      actor={actor}
      actorTarget={atk.actorTarget}
      edits={actorEdits(actor.id, updateCombatantState, resolveStackPolicy(permissions))}
      locked={locked}
      detailed={resolveDetailedActorBodyMap(permissions)}
      roster={roster}
      turnTrackerState={tracker.turnTrackerState}
      rerollInitiative={tracker.rerollInitiative}
      sourceCharacter={atk.attackerSourceCharacter}
      impact={impact}
      isMelee={atk.isMelee}
      onEditArmour={atk.onActorEditArmour}
      onToggleFrenzied={atk.onActorToggleFrenzied}
      chips={{
        slot: atk.slot,
        attackerCharStats: atk.attackerCharStats,
        attackToggles: atk.attackToggles,
        hatred: atk.hatred,
        frenzied: atk.frenzied,
        sourceCharacter: atk.attackerSourceCharacter,
      }}
    />
  );
}

/**
 * The hover curtain (v8.247.0, the user's respec). Your Status view is
 * PRESENT whenever the cursor is not on this panel, and retreats when you
 * come home to work it - once you HAVE worked in it (2026-10-23): on the
 * rails the panel opens from its rail with the cursor outside, and the
 * glass hid the very tab you opened it for. A closed panel forgets. A
 * short grace on leave stops border flicker. The pin (per viewer)
 * disables it.
 *
 * @param {boolean} eligible there is an actor to show, and the Status tab
 *   is not already showing it - projecting the Status view over the
 *   Status tab is noise, and it keeps the status testids unique.
 */
function useHoverCurtain(eligible) {
  const panelOpen = useContext(RailPanelOpenContext);
  const [cursorInside, setCursorInside] = useState(false);
  const [worked, setWorked] = useState(false);
  if (!panelOpen && worked) setWorked(false);
  const leaveTimer = useRef(null);
  useEffect(() => () => clearTimeout(leaveTimer.current), []);
  const viewerPrefs = useSyncExternalStore(subscribeViewerPrefs, getViewerPrefs, getViewerPrefs);
  const auto = viewerPrefs.actorCurtainAuto !== false;
  return {
    auto,
    visible: auto && worked && !cursorInside && eligible,
    onEnter: () => {
      clearTimeout(leaveTimer.current);
      setCursorInside(true);
      setWorked(true);
    },
    onLeave: () => {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = setTimeout(() => setCursorInside(false), 250);
    },
  };
}

/**
 * The header - the character's NAME is the title (v7.13.0); the literal
 * "ATTACKER" named a role in one dice roll, this panel holds a whole
 * person. The actor picks by LOOKING too (v8.240.0): the same card
 * gallery as the target side, in the actor's gold, pick-only, with a
 * No-character card because acting as nobody is legal (ADR 2026-08-30).
 *
 * No lock gates it (2026-10-22): the Edit Weapons lock once disabled it - a
 * leftover from the old profile dropdown - while every other way to act as
 * someone stayed open.
 */
function ActorHeader({ actor, actorTarget, roster, actorId, setActorId, curtainAuto }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <>
      <PanelIdentityHeader
        eyebrow="Acting as"
        name={actor?.name || 'No character'}
        target={actorTarget}
        onNameClick={() => setPickerOpen((o) => !o)}
        nameAriaLabel="Select acting character"
        expanded={pickerOpen}
        trailing={<GhostPin auto={curtainAuto} />}
      />
      {pickerOpen && (
        <CombatantGallery
          roster={peopleOf(roster)}
          value={actorId}
          onSelect={setActorId}
          onClose={() => setPickerOpen(false)}
          title="Acting as"
          ariaLabel="Choose character"
          tone="accent"
          allowNone
        />
      )}
    </>
  );
}

/**
 * The hover curtain's ONE control (v8.247.0): reachable exactly when the
 * curtain is away - it retreats whenever the cursor is on this panel.
 * Unpin = automation off.
 */
function GhostPin({ auto }) {
  return (
    <button
      type="button"
      data-testid="actor-curtain-pin"
      aria-pressed={auto}
      aria-label="Auto-show status when the cursor leaves this panel"
      title={
        auto
          ? 'Your Status view auto-shows whenever the cursor leaves this panel - click to turn that off'
          : 'Auto-show status is OFF - click to turn it back on'
      }
      onClick={() => setViewerPref('actorCurtainAuto', !auto)}
      style={{
        background: 'none',
        border: '1px solid ' + (auto ? 'var(--accent)' : 'var(--border-dim)'),
        borderRadius: 2,
        color: auto ? 'var(--accent)' : 'var(--text-faint)',
        fontSize: 9,
        padding: '0 4px',
        cursor: 'pointer',
        letterSpacing: '0.1em',
      }}
    >
      GHOST
    </button>
  );
}

function TabBar({ panelTab, onTab }) {
  return (
    <div className={common.panelTabBar} role="tablist" aria-label="Attacker panel tabs">
      {TABS.map((t) => (
        <button
          key={t.key}
          role="tab"
          data-testid={`tab-${t.key}`}
          aria-selected={panelTab === t.key}
          className={clsx(common.panelTab, panelTab === t.key && common.panelTabActive)}
          onClick={() => onTab(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** The selected tab's content; the Status tab shows the status view (merged v7.12.0). */
function TabContent({ panelTab, onOpenControls, actor, atk, locked, permissions, statusView }) {
  return (
    <>
      {panelTab === 'armory' && <ArmoryTab onOpenControls={onOpenControls} />}
      {panelTab === 'controls' && <AttackerControlsTab />}
      {/* The acting character's own sheet - same components as the target panel, one view per person. */}
      {SHEET_TABS.has(panelTab) &&
        (actor ? (
          <ActorSheetTabs tab={panelTab} atk={atk} locked={locked} permissions={permissions} />
        ) : (
          <div className={styles.noActorHint}>Pick who you are acting as to see their sheet.</div>
        ))}
      {panelTab === 'overview' && <AttackerOverviewTab />}
      {panelTab === 'status' &&
        (statusView ?? <div className={common.overviewEmpty}>No character loaded - pick who you are acting as.</div>)}
    </>
  );
}

/** Declarative: one prop per edit and ruling the shared sheet tabs take. */
function ActorSheetTabs({ tab, atk, locked, permissions }) {
  // The live sheet of whoever you are acting as. Two locks: what they are, and what has happened to them.
  const { actorId } = useRosterContext();
  const { isLocked } = useSession();
  const sheet = { editor: useSheetEditor(actorId, tab === 'stats'), locked: isLocked('editSheets') };
  const applyConditionModifiers = resolveApplyConditionModifiers(permissions);
  const condModOptions = resolveConditionModOptions(permissions);
  // Tests a condition forbids outright, resolved where the permissions
  // are. The leaves render the map; they do not re-derive the rule.
  const blockedTests = blockedReactionTests(atk.actorTarget, {
    applyConditionMods: applyConditionModifiers,
    condOptions: condModOptions,
    blockSheetTests: resolveConditionsBlockSheetTests(permissions),
  });
  return (
    <CombatantSheetTabs
      tone="actor"
      tab={tab}
      target={atk.actorTarget}
      sourceCharacter={atk.actorSourceCharacter}
      locked={locked}
      tone="actor"
      sheetEditor={sheet.editor}
      sheetLocked={sheet.locked}
      stateLocked={isLocked('editTarget')}
      className={styles.tabContentArea}
      editorWrapClassName={styles.editorWrap}
      applyConditionModifiers={applyConditionModifiers}
      condModOptions={condModOptions}
      blockedTests={blockedTests}
      defenderCustomMods={atk.actorDefenderMods}
      onAddMod={atk.onActorAddMod}
      onEditMod={atk.onActorEditMod}
      onRemoveMod={atk.onActorRemoveMod}
      onToggleFrenzied={atk.onActorToggleFrenzied}
      onSetStandingReaction={atk.onActorSetStandingReaction}
      onTargetChange={atk.onActorTargetChange}
      onEditArmour={atk.onActorEditArmour}
      onReorderWeapons={atk.onActorReorderWeapons}
    />
  );
}

/** The action drum over the panel, and the persistent attack footer - both only with a weapon in hand. */
function AttackBar({ atk, isLocked, roundCountdown }) {
  const { slot, actionDrumOpen, setActionDrumOpen, actionDrumProps } = atk;
  if (!slot || slot.isEmpty) return null;
  return (
    <>
      {actionDrumOpen && (
        <ActionDrum
          open={actionDrumOpen}
          onClose={() => setActionDrumOpen(false)}
          defs={actionDrumProps.defs}
          chamberedId={actionDrumProps.chamberedId}
          disabledReasons={actionDrumProps.disabledReasons}
          engagedIds={actionDrumProps.engagedIds}
          onExecute={actionDrumProps.onExecute}
          notch={actionDrumProps.notch}
        />
      )}
      <AttackFooter
        slot={slot}
        isMelee={atk.isMelee}
        fireMode={atk.fireMode}
        setFireMode={atk.setFireMode}
        fireModes={atk.fireModes}
        handleAttack={atk.handleAttack}
        handleReload={atk.handleReload}
        isLocked={isLocked}
        primaryLabel={atk.attackTriggerLabel}
        actionDrumOpen={actionDrumOpen}
        toggleActionDrum={() => setActionDrumOpen((o) => !o)}
        roundCountdown={roundCountdown}
        economy={atk.actionEconomy}
        onResetActionEconomy={atk.onResetActionEconomy}
      />
    </>
  );
}

export default memo(AttackerPanel);
